import { Track } from "@/src/lib/tracking";
import type {
  BaseResponse,
  FinancialAccount,
  FinancialConnection,
  FinancialConnectionListResponse,
  GetFinancialConnectRequest,
  HttpResponse,
  MemberClient,
} from "@features/api/memberApi";

export type PlaidClientType = "android" | "ios" | "h5" | "web";

export type FinancialConnectionPlatform = "PLAID" | "MANUAL";

export type MemberPlaidApiClient = MemberClient<any>;

export interface FetchMemberPlaidLinkTokenOptions {
  /** Plaid update mode — existing connection internal ID */
  connectionId?: string;
  /** 指定直连的 Broker ID (注入 Plaid link_token 请求，选 broker 时仅需传此项) */
  brokerId?: number;
  /** 授权过期重新连接时的 account_id (用于 update mode / 重新授权) */
  accountId?: string;
  /** @deprecated 请使用 accountId */
  institutionId?: string;
}

const PLAID_INSTITUTION_ALREADY_LINKED_CODE = 146114;

/** FinancialConnection.status: 0=active, 1=disconnected, 2=revoked, 3=deleted */
export const FINANCIAL_CONNECTION_STATUS_ACTIVE = 0;

export const isActiveFinancialConnection = (
  connection: FinancialConnection,
): boolean =>
  (connection.status ?? FINANCIAL_CONNECTION_STATUS_ACTIVE) ===
  FINANCIAL_CONNECTION_STATUS_ACTIVE;

export const filterActiveFinancialConnections = (
  connections: FinancialConnection[],
): FinancialConnection[] => connections.filter(isActiveFinancialConnection);

/** FinancialAccount.status: active when missing or "0" / 0. */
const isActiveFinancialAccount = (account: FinancialAccount): boolean =>
  account.status == null ||
  account.status === "0" ||
  (account.status as unknown) === 0;

/** True when connection has at least one active INVESTMENT sub-account. */
export const connectionHasActiveInvestmentAccount = (
  connection: FinancialConnection,
): boolean =>
  (connection.accounts ?? []).some(
    (account) =>
      account.type === "INVESTMENT" && isActiveFinancialAccount(account),
  );

/** True when connection has at least one active non-INVESTMENT sub-account. */
export const connectionHasActiveNonInvestmentAccount = (
  connection: FinancialConnection,
): boolean =>
  (connection.accounts ?? []).some(
    (account) =>
      account.type != null &&
      account.type !== "INVESTMENT" &&
      isActiveFinancialAccount(account),
  );

/** True when connection has at least one INVESTMENT sub-account (Invest Portfolio chip filter). */
export const connectionHasInvestmentAccount = (
  connection: FinancialConnection,
): boolean =>
  (connection.accounts ?? []).some((account) => account.type === "INVESTMENT");

export const connectionHasNonInvestmentAccount = (
  connection: FinancialConnection,
): boolean =>
  (connection.accounts ?? []).some(
    (account) => account.type != null && account.type !== "INVESTMENT",
  );

/** Invest Portfolio chips: PLAID and MANUAL connections with INVESTMENT accounts only. */
export const filterInvestPortfolioConnections = (
  connections: FinancialConnection[],
): FinancialConnection[] => connections.filter(connectionHasInvestmentAccount);

/** Log connection account status distribution for debugging. */
export const logConnectionAccountStatuses = (
  connection: FinancialConnection,
  platform?: string,
): void => {
  const accounts = connection.accounts ?? [];
  if (accounts.length === 0) {
    return;
  }

  const statusCounts = accounts.reduce(
    (acc, account) => {
      const status = account.status ?? "0";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const hasActive = accounts.some(isActiveFinancialAccount);

  if (!hasActive) {
    Track.log(
      "warn",
      "账户管理",
      `连接无活跃账户: institution=${connection.institution_name} connectionId=${connection.connection_id} platform=${platform || connection.platform} totalAccounts=${accounts.length} statusDistribution=${JSON.stringify(statusCounts)}`,
    );
  } else {
  }
};

/**
 * Invest Portfolio UI (chips + Manage modal): active connections with INVESTMENT sub-accounts.
 * Applies lifecycle filter (status=0) then product filter (INVESTMENT).
 */
export const filterConnectionsForInvestPortfolio = (
  connections: FinancialConnection[],
): FinancialConnection[] =>
  filterInvestPortfolioConnections(
    filterActiveFinancialConnections(connections),
  );

export interface PlaidMemberAuthorizationSuccessOutcome {
  kind: "success";
  connectionId: string;
  connection: FinancialConnection;
}

interface PlaidMemberAuthorizationInstitutionAlreadyLinkedOutcome {
  kind: "institution_already_linked";
  updateLinkToken: string;
}

export type PlaidMemberAuthorizationOutcome =
  | PlaidMemberAuthorizationSuccessOutcome
  | PlaidMemberAuthorizationInstitutionAlreadyLinkedOutcome;

const isFinancialConnectionShape = (
  value: unknown,
): value is FinancialConnection => {
  return (
    value != null &&
    typeof value === "object" &&
    "connection_id" in value &&
    Boolean((value as FinancialConnection).connection_id)
  );
};

/** Backend may return a single connection (OpenAPI) or a list (live API). */
export const connectionFromAuthorizationPayload = (
  payload: unknown,
): FinancialConnection | undefined => {
  if (payload == null || typeof payload !== "object") {
    return undefined;
  }
  if (Array.isArray(payload)) {
    const first = payload[0];
    return isFinancialConnectionShape(first) ? first : undefined;
  }
  return isFinancialConnectionShape(payload) ? payload : undefined;
};

/** Backend may return a single connection (OpenAPI) or a list (live API). */
export const connectionIdFromAuthorizationPayload = (
  payload: unknown,
): string | undefined => {
  return connectionFromAuthorizationPayload(payload)?.connection_id;
};

/** Upsert by `connection_id`: append if missing, replace if exists. */
export const upsertPlaidConnectionInList = (
  prev: FinancialConnection[],
  item: FinancialConnection,
): FinancialConnection[] => {
  const connectionId = item.connection_id;
  if (!connectionId) {
    return prev;
  }

  const existingIndex = prev.findIndex(
    (connection) => connection.connection_id === connectionId,
  );
  if (existingIndex === -1) {
    return [...prev, item];
  }

  return prev.map((connection, index) =>
    index === existingIndex ? item : connection,
  );
};

const fetchFinancialConnections = async (
  memberApi: MemberPlaidApiClient,
  platform: FinancialConnectionPlatform,
): Promise<FinancialConnection[]> => {
  const response = await memberApi.financial.listFinancialConnections(platform);

  if (!response.ok) {
    throw new Error(
      response.statusText ?? `Failed to list ${platform} financial connections`,
    );
  }

  return response.data?.data ?? [];
};

export const fetchPlaidConnections = async (
  memberApi: MemberPlaidApiClient,
): Promise<FinancialConnection[]> =>
  fetchFinancialConnections(memberApi, "PLAID");

/**
 * Convert array of FinancialAccount to single FinancialConnection with all accounts
 * Used for MANUAL platform - all accounts share one connection
 */
const manualAccountsToFinancialConnection = (
  accounts: FinancialAccount[],
): FinancialConnection => {
  // Ensure all accounts are MANUAL platform and set account_mask to last 4 digits of ID
  const manualAccounts = accounts.map((account) => ({
    ...account,
    platform: "MANUAL" as const,
    connection_id: "MANUAL-CONNECTION",
    account_mask: account.account_mask || String(account.id).slice(-4),
  }));

  return {
    connection_id: "MANUAL-CONNECTION", // All manual accounts share this ID
    platform: "MANUAL",
    platform_connection_id: undefined,
    institution_id: undefined,
    institution_name: "Manual Account",
    institution_logo: undefined,
    status: manualAccounts.some((acc) => acc.status === "0") ? 0 : 1, // Active if any account is active
    linked_at: Math.min(
      ...manualAccounts.map((acc) => acc.created ?? Infinity),
    ), // Earliest creation time
    last_synced_time: Math.max(
      ...manualAccounts.map((acc) => acc.last_synced_time ?? 0),
    ), // Latest sync time
    accounts: manualAccounts,
  };
};

export const fetchManualConnections = async (
  memberApi: MemberPlaidApiClient,
): Promise<FinancialConnection[]> => {
  if (typeof memberApi.financial?.listFinancialAccounts !== "function") {
    return fetchFinancialConnections(memberApi, "MANUAL");
  }
  const response = await memberApi.financial.listFinancialAccounts({
    platform: "MANUAL",
    limit: 100,
  });

  if (!response.ok) {
    throw new Error(
      response.statusText ?? "Failed to list MANUAL financial accounts",
    );
  }

  const rows = response.data?.data?.rows ?? [];
  if (rows.length === 0) {
    return [];
  }

  // All manual accounts share one connection
  return [manualAccountsToFinancialConnection(rows)];
};

export const fetchMemberPlaidLinkToken = async (
  memberApi: MemberPlaidApiClient,
  clientType: PlaidClientType,
  options: FetchMemberPlaidLinkTokenOptions = {},
): Promise<string> => {
  const query: GetFinancialConnectRequest = {
    client_type: clientType,
  };
  if (options.connectionId) {
    query.id = options.connectionId;
  }
  if (options.brokerId !== undefined) {
    query.broker_id = options.brokerId;
  }
  const accountId = options.accountId ?? options.institutionId;
  if (accountId) {
    query.account_id = accountId;
  }

  const response = await memberApi.financial.getFinancialConnect(
    "PLAID",
    query,
  );

  if (!response.ok) {
    throw new Error(response.statusText ?? "Failed to get Plaid link token");
  }

  const linkToken = response.data?.data?.link_token;
  if (!linkToken) {
    throw new Error(response.data?.msg ?? "Failed to get Plaid link token");
  }

  return linkToken;
};

const isInstitutionAlreadyLinkedCode = (code: unknown): boolean =>
  Number(code) === PLAID_INSTITUTION_ALREADY_LINKED_CODE;

const authorizationEnvelopeFromHttpResponse = (
  response: HttpResponse<FinancialConnectionListResponse, BaseResponse>,
): BaseResponse | undefined => {
  if (response.ok) {
    return response.data;
  }
  return response.error ?? response.data;
};

const authorizationEnvelopeFromHttpThrow = (
  thrown: unknown,
): BaseResponse | undefined => {
  if (thrown == null || typeof thrown !== "object") {
    return undefined;
  }

  const record = thrown as HttpResponse<
    FinancialConnectionListResponse,
    BaseResponse
  >;
  return record.error ?? record.data;
};

const parsePlaidAuthorizationEnvelope = (
  responseBody: BaseResponse | undefined,
): PlaidMemberAuthorizationOutcome => {
  const code = responseBody?.code;
  const payload: unknown = responseBody?.data;
  const message = responseBody?.msg ?? "Plaid authorization failed";

  if (isInstitutionAlreadyLinkedCode(code)) {
    if (typeof payload === "string" && payload.length > 0) {
      return {
        kind: "institution_already_linked",
        updateLinkToken: payload,
      };
    }
    throw new Error(message);
  }

  if (code != null && Number(code) !== 0) {
    throw new Error(message);
  }

  const connection = connectionFromAuthorizationPayload(payload);
  const connectionId = connection?.connection_id;
  if (!connectionId || !connection) {
    throw new Error(message);
  }

  return { kind: "success", connectionId, connection };
};

export const finalizeMemberPlaidAuthorization = async (
  memberApi: MemberPlaidApiClient,
  clientType: PlaidClientType,
  linkToken: string,
): Promise<PlaidMemberAuthorizationOutcome> => {
  try {
    const response = await memberApi.financial.processFinancialAuthorization(
      "PLAID",
      {
        link_token: linkToken,
        client_type: clientType,
      },
    );

    return parsePlaidAuthorizationEnvelope(
      authorizationEnvelopeFromHttpResponse(response),
    );
  } catch (thrown) {
    const envelope = authorizationEnvelopeFromHttpThrow(thrown);
    if (envelope && isInstitutionAlreadyLinkedCode(envelope.code)) {
      return parsePlaidAuthorizationEnvelope(envelope);
    }

    const message =
      thrown instanceof Error
        ? thrown.message
        : (envelope?.msg ?? "Plaid authorization failed");
    throw new Error(message);
  }
};
